import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  getSurveyWealth,
  getWealthResult,
  sendSurveyWealth,
} from "../../services";
import {
  Box,
  Container,
  IconButton,
  Paper,
  ThemeProvider,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import DialogError from "../../components/DialogError";
import Success from "../Success";
import theme from "../../theme";
import bg1 from "assets/bg_survey_wealth_01.svg";
import bg2 from "assets/bg_survey_wealth_02.svg";
import logo from "assets/logo.svg";
import { LoadingButton } from "@mui/lab";
import SurveyWealthField from "components/form/SurveyWealthField";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { isEnableCapcha } from "helpers/commonFunctions";
import { useSnackbar } from "notistack";
import useDevice from "hooks/useDevice";
import InfoIcon from "@mui/icons-material/Info";

const SurveyWealth = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { isMobile } = useDevice();
  const { surveyId, id } = useParams();
  const [data, setData] = useState();
  const [error, setError] = useState();
  const [success, setSuccess] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const [loading, setLoading] = useState(false);
  const isSubmitted = queryParams.get("isSubmitted");

  useEffect(() => {
    if (isSubmitted) {
      setSuccess(true);
    }
  }, [isSubmitted]);

  const schema = useMemo(() => {
    if (data) {
      const validate = {};
      data?.children?.forEach((question) => {
        if (question.isRequire) {
          if (question.subType === "TEXT_QUESTION") {
            validate[question.elementId] = yup
              .array()
              .of(
                yup.object().shape({
                  text: yup.string().required("Không được để trống"),
                })
              )
              .required("Không được để trống");
          } else {
            validate[question.elementId] = yup
              .array()
              .of(
                yup.object().shape({
                  answerId: yup.string().required("Vui lòng chọn đáp án"),
                })
              )
              .min(1, "Vui lòng chọn đáp án")
              .required("Vui lòng chọn đáp án");
          }
        } else {
          validate[question.elementId] = yup.array();
        }
        if (question.subType === "TEXT_QUESTION") {
          validate[question.elementId] = (
            validate[question.elementId] || yup.array()
          ).test("", "Tối đa 2000 ký tự", function (value) {
            if (value && value[0]?.text) {
              return value[0].text.length <= 2000;
            }
            return true;
          });
        } else {
          const allColumns = question.children
            ?.filter((c) => c.subType === "ROW_QUESTION")
            .flatMap((row) => row.children || []);
          question.children.forEach((answer) => {
            if (answer.subType === "ROW_QUESTION") {
              validate[question.elementId] = yup.array().of(
                yup.object({
                  answerId: yup.number().required(),
                  text: yup.mixed().test("validate-cell", function (value) {
                    const answerId = this.parent.answerId;
                    const cell = allColumns?.find(
                      (c) => c.elementId === answerId
                    );
                    if (!cell) return true;

                    if (cell.subType === "NUMBER_ANSWER") {
                      const num = Number(value);
                      if (isNaN(num)) {
                        return this.createError({
                          message: {
                            answerId,
                            message: `Phải nhập số`,
                          },
                        });
                      }
                      if (
                        (cell.min != null && num < cell.min) ||
                        (cell.max != null && num > cell.max)
                      ) {
                        return this.createError({
                          message: {
                            answerId,
                            message: `Giá trị phải ${
                              cell.min != null && cell.max != null
                                ? `nằm trong khoảng từ ${cell.min} đến ${cell.max}`
                                : cell.min != null
                                ? `lớn hơn hoặc bằng ${cell.min}`
                                : cell.max != null
                                ? `nhỏ hơn hoặc bằng ${cell.max}`
                                : `hợp lệ`
                            }`,
                          },
                        });
                      }
                    }
                    return true;
                  }),
                })
              );
            }
          });
        }
      });
      return yup.object(validate).required();
    }
  }, [data]);

  const listQuestions = useMemo(() => {
    if (data && data.children.length > 0) {
      return data.children.filter((q) => q.type === "QUESTION");
    }
  }, [data]);
  const {
    control,
    setFocus,
    formState: { errors },
    handleSubmit,
  } = useForm({
    mode: "all",
    resolver: yupResolver(schema),
  });

  const hasAnswer = useMemo(() => {
    return (
      data?.children &&
      data?.children?.some((q) =>
        q?.children?.some((a) => a.type === "ANSWER" && a.answerText !== null)
      )
    );
  }, [data?.children]);

  React.useEffect(() => {
    const firstError = Object.keys(errors).reduce((field, a) => {
      return !!errors[field] ? field : a;
    }, null);

    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);
  useEffect(() => {
    if (surveyId && id) {
      getSurveyWealth(surveyId, id)
        .then((data) => {
          if (data) {
            setData(data);
          }
        })
        .catch((e) => {
          if (e.response.data.errorCode === 500) {
            enqueueSnackbar(e.response.data.message, {
              variant: "error",
              autoHideDuration: 3000,
            });
          }
        });
    }
  }, [surveyId, id, enqueueSnackbar]);
  const handleOnSubmit = async (value) => {
    let token;
    if (isEnableCapcha()) {
      if (!executeRecaptcha) {
        console.error("Execute recaptcha not yet available");
        return;
      }
      token = await executeRecaptcha("submit");
    }
    setLoading(true);
    const responseList = Object.keys(value).map((key) => ({
      questionId: Number(key),
      answerList: value[key],
    }));
    sendSurveyWealth(surveyId, id, {
      captcha: token,
      responseList: responseList,
    })
      .then(() => {
        setLoading(false);
        setSuccess(true);
        if (data?.channel === "NEO") {
          const currentUrl = new URL(window.location.href);
          if (!currentUrl.searchParams.has("isSubmitted")) {
            window.location.href = currentUrl.href + "?isSubmitted=true";
          } else {
            window.location.href = currentUrl.href;
          }
        }
      })
      .catch((e) => {
        setLoading(false);
        setError(e.response.data.message);
      });
  };
  const getReportUrl = () => {
    const currentUrl = new URL(window.location.href);
    return currentUrl.origin + currentUrl.pathname + "/report";
  };

  if (success) {
    return (
      <Success isNEO={data?.channel === "NEO"} navigateUrl={getReportUrl()} />
    );
  }
  if (!data) {
    return (
      <DialogError open={error} onClose={() => setError("")} message={error} />
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ background: "#f7f7f7", height: "100vh", overflowX: "hidden" }}>
        <Container className="md:!py-8 relative !px-0 md:!px-6">
          <Paper
            component="form"
            onSubmit={handleSubmit(handleOnSubmit)}
            elevation={1}
            className="bg-white lg:!py-[26px] lg:!px-8 !p-4 !rounded-lg relative"
          >
            <img
              src={bg1}
              alt="decor"
              className="absolute top-[86px] right-0 z-0"
            />
            <img
              src={bg2}
              alt="decor"
              className="absolute bottom-[0] left-0 z-0"
            />
            <img src={logo} alt="VPBank" className="w-[140px]" />
            <div className="pt-[25px] font-bold text-[1.125rem] md:text-center uppercase text-[#00B74F]">
              {data?.title}
              {isMobile && (
                <IconButton
                  sx={{ padding: "0", marginLeft: "8px", marginBottom: "2px" }}
                  onClick={() => setShowDesc(!showDesc)}
                >
                  <InfoIcon
                    sx={{
                      color: showDesc ? "#00AD53" : "#0B263D",
                      fontSize: "16px",
                    }}
                  />
                </IconButton>
              )}
            </div>
            <div
              className={"pt-2 description-survey-wealth ".concat(
                isMobile
                  ? `!p-4 !mt-4 rounded-2xl bg-[#EDFFF4] max-h-[300px] overflow-y-auto ${
                      !showDesc ? "hidden" : ""
                    }`
                  : ""
              )}
              dangerouslySetInnerHTML={{ __html: data?.description }}
            />
            <div className="py-6">
              {data.children.map((question, index) => (
                <SurveyWealthField
                  question={question}
                  questionIdx={listQuestions.findIndex(
                    (q) => q.elementId === question.elementId
                  )}
                  control={control}
                  index={index}
                  key={question.elementId}
                  hasAnswer={hasAnswer}
                />
              ))}
            </div>
            {!hasAnswer ? (
              <div className="mx-[-32px] flex justify-center pt-6 pb-[89px] gap-6 border-t-[1px] border-t-[#E8E8E8]">
                <LoadingButton
                  loading={loading}
                  className="!rounded-[4px] !p-[10px] !font-semibold !text-[0.875rem] !leading-normal !tracking-[0.03em]"
                  type="submit"
                  sx={{
                    background:
                      "linear-gradient(270deg, #00B74F -22.41%, #1D4289 108.33%)",
                  }}
                  variant="contained"
                  color="primary"
                >
                  Lưu & Submit
                </LoadingButton>
              </div>
            ) : (
              <div className="mx-[-32px] flex justify-center pt-6 pb-[89px]"></div>
            )}
          </Paper>
        </Container>
        <DialogError
          open={error}
          onClose={() => setError("")}
          message={error}
        />
      </Box>
    </ThemeProvider>
  );
};

export default SurveyWealth;
